import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Link,
  Stack,
  Avatar,
  Rating,
  IconButton,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  MapPin,
  Phone,
  Globe,
  InstagramLogo,
  FacebookLogo,
  TwitterLogo,
  PinterestLogo,
  TiktokLogo,
  YoutubeLogo,
  Clock,
  Star,
  Image as ImageIcon,
  Calendar,
  ChatCircleText,
  CaretLeft,
  CaretRight,
  X,
  Envelope,
} from '@phosphor-icons/react';
import { useTheme } from '@mui/material/styles';


import PublicLeadForm from '../Business/PublicLeadForm';
import api from '../../utils/api';
import { getStaffMemberDisplayPhoto } from '../../utils/avatarConstants';
import { supplierCategoryLabels, supplierCategoryGroups } from '../../config/supplierTaxonomy';
import useThemeStyles from '../../theme/useThemeStyles';

// Recommended Suppliers Section Component
function RecommendedSuppliersSection({ supplierIds, isPublic }) {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoading(true);
        // Normalize IDs - handle both string IDs and ObjectId objects
        const normalizedIds = supplierIds.map(id => {
          if (typeof id === 'string') return id;
          if (id && id._id) return id._id.toString();
          if (id && id.toString) return id.toString();
          return id;
        }).filter(Boolean);

        if (normalizedIds.length === 0) {
          setLoading(false);
          return;
        }

        const supplierPromises = normalizedIds.map(async (id) => {
          try {
            // For in-app view, try supplier endpoint first (works with any status)
            if (!isPublic) {
              try {
                const response = await api.get(`/api/supplier/${id}`);
                const data = response.data;
                // Normalize data format
                return {
                  ...data,
                  name: data.name || data.businessName,
                  media: {
                    ...(data.media || {}),
                    logo: data.media?.logo || data.imageUrl
                  }
                };
              } catch (supplierError) {
                // Fall back to public endpoint
                const response = await api.get(`/api/public/business/${id}`);
                return response.data;
              }
            } else {
              // For public view, use public endpoint
              const response = await api.get(`/api/public/business/${id}`);
              return response.data;
            }
          } catch (error) {
            return null;
          }
        });
        const results = await Promise.all(supplierPromises);
        const validSuppliers = results.filter(supplier => supplier !== null);
        setSuppliers(validSuppliers);
      } catch (error) {} finally {
        setLoading(false);
      }
    };

    if (supplierIds && supplierIds.length > 0) {
      fetchSuppliers();
    } else {
      setLoading(false);
    }
  }, [supplierIds, isPublic]);

  const categoryLabels = supplierCategoryLabels;

  if (loading) {
    return (
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (suppliers.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mb: 4, borderRadius: 2 }}>
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom>
          Recommended Suppliers
        </Typography>
        <Grid container spacing={3}>
          {suppliers.map((supplier) => (
            <Grid item xs={12} sm={6} md={4} key={supplier._id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
                onClick={() => {
                  if (isPublic) {
                    window.location.href = `/profile/${supplier._id}${supplier.slug ? `/${supplier.slug}` : ''}`;
                  } else {
                    navigate(`/dashboard/supplier/${supplier._id}`);
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      src={supplier.media?.logo || supplier.imageUrl}
                      sx={{ width: 60, height: 60 }}
                    >
                      {(supplier.name || supplier.businessName || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {supplier.name || supplier.businessName || 'Unknown Supplier'}
                      </Typography>
                      <Chip
                        label={categoryLabels[supplier.category] || supplier.category}
                        size="small"
                        color="primary"
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPublic) {
                        window.location.href = `/profile/${supplier._id}${supplier.slug ? `/${supplier.slug}` : ''}`;
                      } else {
                        navigate(`/dashboard/supplier/${supplier._id}`);
                      }
                    }}
                  >
                    View Profile
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

function UnifiedSupplierProfile({ 
  supplier, 
  loading, 
  error, 
  isPublic = false,
  formData = null,
  onEnquireClick = null,
}) {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [_filesLoading, _setFilesLoading] = useState(false);
  const [_filesError, _setFilesError] = useState('');
  const [_filesData, _setFilesData] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);
  const [_staffMembersLoading, setStaffMembersLoading] = useState(false);

  // Get all images (cover + gallery)
  const getAllImages = () => {
    const images = [];
    if (supplier?.coverImage || supplier?.media?.coverImage) {
      images.push(supplier.coverImage || supplier.media.coverImage);
    }
    const gallery = supplier?.gallery || supplier?.media?.gallery || [];
    gallery.forEach(img => {
      if (!images.includes(img)) images.push(img);
    });
    return images;
  };

  const images = getAllImages();
  const _hasImages = images.length > 0;
  const galleryImages = supplier?.gallery || supplier?.media?.gallery || [];
  const coverImage = supplier?.coverImage || supplier?.media?.coverImage;

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const handleCloseLightbox = () => {
    setSelectedImageIndex(null);
  };

  const handlePreviousImage = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e) => {
    if (selectedImageIndex === null) return;
    if (e.key === 'ArrowLeft') handlePreviousImage(e);
    if (e.key === 'ArrowRight') handleNextImage(e);
    if (e.key === 'Escape') handleCloseLightbox();
  };

  useEffect(() => {
    if (selectedImageIndex !== null) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedImageIndex]);

  const capitalizeCategory = (category) => {
    if (!category) return 'Uncategorized';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const categoryLabels = supplierCategoryLabels;

  const priceRangeLabels = {
    '£': 'Budget Friendly',
    '££': 'Mid Range',
    '£££': 'Premium',
    '££££': 'Luxury',
  };

  const getCategoryLabel = (category) => {
    return categoryLabels[category] || capitalizeCategory(category);
  };

  const formatForBadge = (val) => {
    if (val === true) return 'Yes';
    if (val === false || val == null) return null;
    if (typeof val === 'number') return String(val);
    if (typeof val === 'string') return val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return null;
  };

  const extractDetailValues = (details) => {
    const values = [];
    if (!details) return values;
    if (typeof details === 'string') {
      const f = formatForBadge(details);
      return f ? [f] : [];
    }
    if (typeof details !== 'object') return values;
    Object.entries(details).forEach(([key, v]) => {
      if (Array.isArray(v)) {
        v.forEach(item => {
          const formatted = formatForBadge(item);
          if (formatted) values.push(formatted);
        });
      } else if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
        if ('min' in v && 'max' in v) values.push(`${v.min}-${v.max} guests`);
      } else if (key === 'travelRadius' && typeof v === 'number') {
        values.push(`${v} miles`);
      } else {
        const formatted = formatForBadge(v);
        if (formatted) values.push(formatted);
      }
    });
    return values;
  };

  const renderDetailBadges = (details) => {
    const values = extractDetailValues(details);
    if (values.length === 0) return null;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {values.map((val, i) => (
          <Chip key={i} label={val} size="small" variant="outlined" />
        ))}
      </Box>
    );
  };

  const getSocialMediaLinks = () => {
    const socialMedia = supplier?.socialMedia || {};
    const links = [];
    
    // Helper function to validate URL
    const isValidUrl = (url) => {
      if (!url || typeof url !== 'string') return false;
      const trimmed = url.trim();
      return trimmed.startsWith('http://') || trimmed.startsWith('https://');
    };
    
    if (socialMedia.instagram && isValidUrl(socialMedia.instagram)) {
      links.push({ name: 'Instagram', url: socialMedia.instagram.trim(), icon: InstagramLogo });
    }
    if (socialMedia.facebook && isValidUrl(socialMedia.facebook)) {
      links.push({ name: 'Facebook', url: socialMedia.facebook.trim(), icon: FacebookLogo });
    }
    if (socialMedia.twitter && isValidUrl(socialMedia.twitter)) {
      links.push({ name: 'Twitter', url: socialMedia.twitter.trim(), icon: TwitterLogo });
    }
    if (socialMedia.pinterest && isValidUrl(socialMedia.pinterest)) {
      links.push({ name: 'Pinterest', url: socialMedia.pinterest.trim(), icon: PinterestLogo });
    }
    if (socialMedia.tiktok && isValidUrl(socialMedia.tiktok)) {
      links.push({ name: 'TikTok', url: socialMedia.tiktok.trim(), icon: TiktokLogo });
    }
    if (socialMedia.youtube && isValidUrl(socialMedia.youtube)) {
      links.push({ name: 'YouTube', url: socialMedia.youtube.trim(), icon: YoutubeLogo });
    }
    return links;
  };

  const socialLinks = getSocialMediaLinks();
  const businessName = supplier?.businessName || supplier?.name;
  const category = supplier?.category;
  const priceRange = supplier?.priceRange;
  const address = supplier?.address || supplier?.contactInfo?.address;
  const ratings = supplier?.ratings;
  const isVerified = supplier?.verification?.isVerified;
  
  // Memoize rating value to prevent infinite re-renders
  const ratingValue = useMemo(() => {
    if (!ratings?.average) return 0;
    const value = Number(ratings.average);
    if (isNaN(value) || value < 0) return 0;
    // Clamp value between 0 and 5 (valid rating range)
    return Math.min(Math.max(value, 0), 5);
  }, [ratings?.average]);

  // Fetch staff members with showOnProfile
  useEffect(() => {
    const fetchStaffMembers = async () => {
      // Skip if public profile - staff members should come from supplier data
      if (isPublic) {
        // Use staff members from supplier.teamMembers if available
        if (supplier?.teamMembers && Array.isArray(supplier.teamMembers)) {
          const visibleStaff = supplier.teamMembers.filter(
            tm => tm.showOnProfile && tm.status === 'active'
          );
          setStaffMembers(visibleStaff);
        } else {
          setStaffMembers([]);
        }
        return;
      }
      
      if (!supplier?._id && !supplier?.business?._id) return;
      
      const businessId = supplier?._id || supplier?.business?._id;
      if (!businessId) return;

      setStaffMembersLoading(true);
      try {
        const response = await api.get(`/api/supplier/staff-members?businessId=${businessId}`);
        // Filter to only show staff members with showOnProfile: true
        const visibleStaff = (response.data.staffMembers || []).filter(
          sm => sm.showOnProfile && sm.status === 'active'
        );
        setStaffMembers(visibleStaff);
      } catch (err) {
        // Don't show error - just don't display staff members
        setStaffMembers([]);
      } finally {
        setStaffMembersLoading(false);
      }
    };

    fetchStaffMembers();
    // Use stable identifier instead of entire supplier object to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier?._id, supplier?.business?._id, isPublic, supplier?.teamMembers]);

  // Debug logging for recommended suppliers
  useEffect(() => {
    if (supplier) {}
    // Use stable identifier instead of entire supplier object to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier?._id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!supplier) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Image Gallery Container - Airbnb Style */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              height: { xs: 300, md: 400 },
              borderRadius: 0.5,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Left Side - Cover Photo */}
            <Box
              sx={{
                flex: { xs: '1 1 100%', md: '1 1 60%' },
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'grey.200',
                cursor: coverImage ? 'pointer' : 'default',
                '&:hover': coverImage ? {
                  '& .overlay': {
                    opacity: 1,
                  }
                } : {},
              }}
              onClick={() => coverImage && handleImageClick(0)}
            >
              {coverImage ? (
                <>
                  <Box
                    component="img"
                    src={coverImage}
                    alt={businessName}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    className="overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: 'rgba(0, 0, 0, 0.1)',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    }}
                  />
                </>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  }}
                >
                  <ImageIcon size={48} color="white" />
                </Box>
              )}
            </Box>

            {/* Right Side - Gallery Images Grid (4-grid) */}
            {galleryImages.length > 0 && (
              <Box
                sx={{
                  flex: { xs: '0 0 0', md: '1 1 40%' },
                  display: { xs: 'none', md: 'grid' },
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1,
                  height: '100%',
                }}
              >
                {galleryImages.slice(0, 4).map((image, index) => {
                  const imageIndex = coverImage ? index + 1 : index;
                  return (
                    <Box
                      key={index}
                      sx={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: 'grey.200',
                        cursor: 'pointer',
                        '&:hover': {
                          '& .overlay': {
                            opacity: 1,
                          },
                          '& img': {
                            transform: 'scale(1.05)',
                          }
                        },
                        transition: 'all 0.2s',
                      }}
                      onClick={() => handleImageClick(imageIndex)}
                    >
                      <Box
                        component="img"
                        src={image}
                        alt={`Gallery image ${index + 1}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.2s',
                        }}
                      />
                      <Box
                        className="overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(0, 0, 0, 0.1)',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                        }}
                      />
                    </Box>
                  );
                })}
                {images.length > 5 && (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      bgcolor: 'grey.900',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        bgcolor: 'grey.800',
                      },
                      transition: 'background-color 0.2s',
                    }}
                    onClick={() => handleImageClick(coverImage ? 5 : 4)}
                  >
                    <Box
                      component="img"
                      src={galleryImages[4] || coverImage}
                      alt="Gallery image"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.7,
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                        }}
                      >
                        Show all {images.length} photos
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Business Name Container */}
          <Box sx={{ mt: 3, mb: 2, px: { xs: 0, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, mb: 1 }}>
                  {businessName}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={getCategoryLabel(category)}
                    color="primary"
                    size="small"
                  />
                  {priceRange && (
                    <Chip
                      label={priceRangeLabels[priceRange]}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {isVerified && (
                    <Chip
                      label="Verified"
                      size="small"
                      icon={<Star size={16} weight="fill" />}
                    />
                  )}
                  {address && (
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MapPin size={16} />
                      {typeof address === 'string' ? address : [
                        address.city,
                        address.state,
                      ].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  {ratings?.count > 0 && ratingValue > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Rating value={ratingValue} readOnly precision={0.1} size="small" />
                      <Typography variant="body2" color="text.secondary">
                        {ratingValue.toFixed(1)} ({ratings.count} reviews)
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
              {/* Contact and Enquire buttons (shown for both in-app and public/couple view) */}
              {onEnquireClick && (
                <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Calendar size={20} />}
                    onClick={onEnquireClick}
                    size="large"
                  >
                    Book This Supplier
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ChatCircleText size={20} />}
                    onClick={() => navigate(`/chat?supplier=${supplier._id || supplier.id}`)}
                  >
                    Chat
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={isPublic && formData ? 8 : 12}>
            {/* Description */}
            {(supplier.description || supplier.businessDescription) && (
              <Card sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h5" component="h2" gutterBottom>
                    About Us
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {supplier.description || supplier.businessDescription}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Team Members - Now using StaffMembers */}
            {staffMembers.length > 0 && (
              <Card sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h5" component="h2" gutterBottom>
                    Meet the Team
                  </Typography>
                  <Grid container spacing={3}>
                    {staffMembers.map((member) => {
                      const displayName = member.user 
                        ? `${member.user.firstName || ''} ${member.user.lastName || ''}`.trim() || member.name
                        : member.name;
                      const avatarSrc = getStaffMemberDisplayPhoto(member);
                      const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
                      
                      return (
                        <Grid item xs={12} sm={6} md={4} key={member._id}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Avatar
                              src={avatarSrc}
                              sx={{ width: 120, height: 120, mb: 2 }}
                            >
                              {initials}
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {displayName}
                            </Typography>
                            {member.role && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {member.role}
                              </Typography>
                            )}
                            {member.notes && (
                              <Typography variant="body2" color="text.secondary">
                                {member.notes}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Recommended Suppliers */}
            {(() => {
              const recommendedSuppliers = supplier.recommendedSuppliers;
              const hasRecommendedSuppliers = recommendedSuppliers && 
                Array.isArray(recommendedSuppliers) && 
                recommendedSuppliers.length > 0;
              
              if (!hasRecommendedSuppliers) return null;
              
              return (
                <RecommendedSuppliersSection
                  supplierIds={recommendedSuppliers}
                  isPublic={isPublic}
                />
              );
            })()}

            {/* Category-Specific Details */}
            {(() => {
              const category = supplier?.category;
              const renderCategoryDetails = () => {
                // Venue Details
                if (category === 'venue' && supplier.venueDetails && Object.keys(supplier.venueDetails).length > 0) {
                  const badges = renderDetailBadges(supplier.venueDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                // Media Details (Photographer/Videographer)
                if ((supplierCategoryGroups.media || []).includes(category) && supplier.mediaDetails) {
                  const mediaDetails = supplier.mediaDetails;
                  const badges = renderDetailBadges(mediaDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                // Decor Details
                if ((supplierCategoryGroups.decorLike || []).includes(category) && supplier.decorDetails) {
                  const decorDetails = supplier.decorDetails;
                  const badges = renderDetailBadges(decorDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                // Food Details (Caterer/Cake)
                if ((category === 'caterer' || category === 'cake') && supplier.foodDetails) {
                  const foodDetails = supplier.foodDetails;
                  const badges = renderDetailBadges(foodDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                // Fashion Details (Dress/Suit)
                if ((category === 'dress' || category === 'suit') && supplier.fashionDetails) {
                  const fashionDetails = supplier.fashionDetails;
                  const badges = renderDetailBadges(fashionDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                // Beauty Details (Hair/Makeup)
                if ((category === 'hair' || category === 'makeup') && supplier.beautyDetails) {
                  const beautyDetails = supplier.beautyDetails;
                  const badges = renderDetailBadges(beautyDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                // Entertainment Details (Band/DJ/Photobooths/Bar/Fireworks etc.)
                if ((supplierCategoryGroups.entertainmentLike || []).includes(category) && 
                    supplier.entertainmentDetails && Object.keys(supplier.entertainmentDetails).length > 0) {
                  const badges = renderDetailBadges(supplier.entertainmentDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                // Florist Details
                if (category === 'florist' && supplier.floristDetails) {
                  const floristDetails = supplier.floristDetails;
                  const badges = renderDetailBadges(floristDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                // Planner Details
                if ((supplierCategoryGroups.plannerLike || []).includes(category) && supplier.plannerDetails) {
                  const plannerDetails = supplier.plannerDetails;
                  const badges = renderDetailBadges(plannerDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                if ((category === 'transport' || category === 'accommodation') && supplier.otherDetails && Object.keys(supplier.otherDetails).length > 0) {
                  const badges = renderDetailBadges(supplier.otherDetails);
                  if (!badges) return null;
                  return (
                    <Card sx={{ mb: 4, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getCategoryLabel(category)} Details
                        </Typography>
                        {badges}
                      </CardContent>
                    </Card>
                  );
                }

                return null;
              };

              return renderCategoryDetails();
            })()}

            {/* FAQ */}
            {supplier.faqs && supplier.faqs.length > 0 && (
              <Card sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h5" component="h2" gutterBottom>
                    Frequently Asked Questions
                  </Typography>
                  {supplier.faqs.map((item, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {item.question}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.answer}
                      </Typography>
                      {index < supplier.faqs.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Policies */}
            {supplier.policies && Object.keys(supplier.policies).length > 0 && (
              <Card sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h5" component="h2" gutterBottom>
                    Policies & Terms
                  </Typography>
                  <Grid container spacing={3}>
                    {Object.entries(supplier.policies).map(([key, value]) => {
                      if (!value || value.trim() === '') return null;
                      return (
                        <Grid item xs={12} sm={6} key={key}>
                          <Typography variant="subtitle1" gutterBottom sx={{ textTransform: 'capitalize' }}>
                            {key.replace(/([A-Z])/g, ' $1').trim()} Policy
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {value}
                          </Typography>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Contact Information */}
            <Card sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom>
                  Contact Information
                </Typography>
                <Grid container spacing={4}>
                  {/* Left Column - Contact Details */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      {supplier.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone size={20} />
                          <Link href={`tel:${supplier.phone}`} color="inherit">
                            {supplier.phone}
                          </Link>
                        </Box>
                      )}
                      {supplier.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Envelope size={20} />
                          <Link href={`mailto:${supplier.email}`} color="inherit">
                            {supplier.email}
                          </Link>
                        </Box>
                      )}
                      {supplier.website && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Globe size={20} />
                          <Link 
                            href={supplier.website.startsWith('http') ? supplier.website : `https://${supplier.website}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            color="inherit"
                          >
                            Visit Website
                          </Link>
                        </Box>
                      )}
                      {address && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <MapPin size={20} style={{ marginTop: 2 }} />
                          <Typography variant="body2">
                            {typeof address === 'string' ? address : [
                              address.street,
                              address.city,
                              address.state,
                              address.postalCode,
                            ].filter(Boolean).join(', ')}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Grid>

                  {/* Right Column - Opening Hours */}
                  <Grid item xs={12} md={6}>
                    {supplier.openingHours && (() => {
                      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                      const dayLabels = {
                        monday: 'Monday',
                        tuesday: 'Tuesday',
                        wednesday: 'Wednesday',
                        thursday: 'Thursday',
                        friday: 'Friday',
                        saturday: 'Saturday',
                        sunday: 'Sunday',
                      };
                      const openDays = days.filter(day => supplier.openingHours[day]?.isOpen);
                      
                      if (openDays.length === 0) return null;
                      
                      return (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Clock size={20} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Opening Hours
                            </Typography>
                          </Box>
                          <Stack spacing={1}>
                            {openDays.map((day) => {
                              const hours = supplier.openingHours[day];
                              return (
                                <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2">{dayLabels[day]}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {hours.openTime} - {hours.closeTime}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      );
                    })()}
                  </Grid>
                </Grid>

                {/* Social Media */}
                {socialLinks.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Follow Us
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {socialLinks.map((social) => {
                        const Icon = social.icon;
                        return (
                          <IconButton
                            key={social.name}
                            component="a"
                            href={social.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main' }}
                          >
                            <Icon size={24} />
                          </IconButton>
                        );
                      })}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Sidebar - Form/Enquiry */}
          {isPublic && formData && (
            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'sticky', top: 20, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" component="h3" gutterBottom>
                    Get in Touch
                  </Typography>
                  <PublicLeadForm formData={formData} businessId={supplier._id || supplier.id} />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Image Carousel Lightbox */}
      <Dialog
        open={selectedImageIndex !== null}
        onClose={handleCloseLightbox}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            maxWidth: '100vw',
            maxHeight: '100vh',
            m: 0,
            borderRadius: 0,
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Close Button */}
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <X size={24} />
          </IconButton>

          {/* Previous Button */}
          {images.length > 1 && (
            <IconButton
              onClick={handlePreviousImage}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <CaretLeft size={32} />
            </IconButton>
          )}

          {/* Next Button */}
          {images.length > 1 && (
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <CaretRight size={32} />
            </IconButton>
          )}

          {/* Image Counter */}
          {images.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 0.5,
              }}
            >
              <Typography variant="body2">
                {selectedImageIndex !== null ? selectedImageIndex + 1 : 0} / {images.length}
              </Typography>
            </Box>
          )}

          {/* Image */}
          {selectedImageIndex !== null && images[selectedImageIndex] && (
            <Box
              component="img"
              src={images[selectedImageIndex]}
              alt={`Image ${selectedImageIndex + 1}`}
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default UnifiedSupplierProfile;

